import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  HostBinding, EventEmitter, ElementRef, Input, OnInit,
  OnChanges, OnDestroy, Output, ViewChild, SimpleChanges,
} from '@angular/core';
import * as _ from 'lodash';
import { Observable } from 'rxjs';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { currentMomentString } from 'utils/date';

import { ActiveFilterModel } from '../active-filters/active-filter.model';
import {
  PART_ATTRIBUTES_FILTER_TYPE, PADB_FILTER_TYPE, CUSTOM_FILTER_TYPE
} from '../active-filters/active-filter-types';
import { createOption, defaultOptionValue, allOptionValue } from '../../form/select/option-helpers';
import { SIMPLE_TABLE_MODAL_NAME } from '../../modal/modal-names';
import { getFilterTitle } from '../../search/active-filters/active-filters.helpers';
import { AdvancedSearchService } from '../../search/advanced-search/advanced-search.service';

import { SelectDropdownComponent } from '../../dropdown/select-dropdown/select-dropdown.component';

import { AppModalService } from '../../modal/modal.service';
import { partAttributesTypes } from './part-attributes-types';
import { langStub } from './lang-stub';
import { attrOptions, PADB_OPT, CUSTOM_OPT, customAttrOptions } from './attribute-options';


@Component({
  moduleId: module.id,
  selector: 'part-attributes-search',
  templateUrl: 'part-attributes-search.component.html',
  styleUrls: ['part-attributes-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartAttributesSearchComponent implements OnDestroy {

  @Input() showAddAnotherBtn: boolean = true;
  @Input() filters: Array<ActiveFilterModel> = [];
  @Input() ghostValue: string;
  @Input() attributesOptions: Array<any> = [];
  @Input() filterSearchModel: any = {};
  @Input() filterPlaceholders: any;
  @Input() status: string;
  @Input() brandId: string;

  @Output() fetchAttributeOptions: EventEmitter<any> = new EventEmitter();
  @Output() fetchAttributeFromOptions: EventEmitter<any> = new EventEmitter();
  @Output() fetchProcess: EventEmitter<any> = new EventEmitter();
  @Output() removePartAttribute: EventEmitter<any> = new EventEmitter();
  @Output() enterClicked: EventEmitter<any> = new EventEmitter();

  @ViewChild('attributesDropdown', {static: false})
  attributesDropdown: SelectDropdownComponent | void;

  @ViewChild('attributeSearchInput', {static: false})
  attributeSearchInput: ElementRef | void;

  attrFormGroup: UntypedFormGroup;
  isActive: boolean = true;
  modalTitle: string = 'Relevant Additional Criteria for your selection';
  partAttributesTypes = partAttributesTypes;
  partAttributesPlaceholder: string = 'Select a field';

  constructor(
    private fb: UntypedFormBuilder,
    private ref: ChangeDetectorRef,
    private appModalService: AppModalService,
    private advancedSearchService: AdvancedSearchService,
  ) {
    this.attrFormGroup = this.fb.group({
      attributes: this.fb.array([])
    });
  }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get attributes(): UntypedFormArray {
    return this.attrFormGroup.get('attributes') as UntypedFormArray;
  }

  get partAttributes(): Array<ActiveFilterModel> {
    return _.reduce(this.attributes.value, (result: Array<any>, data: any) => {
      if (!data.attributeOption) { return result; }
      // check if all require fields are filled
      if (data.attribute.nested_fields && data.nestedAttributes && data.nestedAttributes.length && !data.checkNullValue) {
        if (!_.find(data.nestedAttributes, (item: any) => {
          return item.selectValue !== null;
        })) { return result; }
      } else if (data.attribute.is_text_select) {
        if (!data.checkNullValue && (data.textValue === null || data.selectValue === null)) {
          return result;
        }
        if (data.checkNullValue && data.selectValue === null) {
          return result;
        }
      } else if (data.attribute.is_text_input_only) {
        if (!data.checkNullValue && data.textValue === null) {
          return result;
        }
      } else if (data.dateFromValue === null && data.dateToValue === null
        && data.selectValue === null && !data.checkNullValue) {
        return result;
      }
      const filter = this.getPartAttributesFilter(data);
      result.push(filter);
      return result;
    }, []);
  }

  get partAttributesFilters(): Array<ActiveFilterModel> {
    return _.filter(this.filters, (filter: any) => {
      return filter.type === PART_ATTRIBUTES_FILTER_TYPE;
    });
  }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnDestroy() {
    this.isActive = false;
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  public attributesItemsWithHideStatus(control: UntypedFormControl): Array<any> {
    // return the same array instead of creating new one via map
    _.each(this.attributesOptions, (item: any) => {
      const sameName = control.value ? (item.title.toLowerCase() === control.value.toLowerCase()) :
        false;
      item.hidden = this.isAlreadyUsed(item._id) || sameName;
      return item;
    });
    return this.attributesOptions;
  }

  public resetAttributes(): void {
    if (!this.attributes.length || this.attributes.length > 1 ||
        (this.attributes.length && this.attributes.controls[0].get('attributeOption').value)
     ) {
      this.resetFormGroup();
      if (!this.partAttributesFilters.length) {
        _.defer(() => this.addAttributeToFormArray());
      }
    }
  }

  public resetFormGroup(): void {
    this.attrFormGroup.setControl('attributes', this.fb.array([]));
  }

  public getPartAttributesFilter(data: any = {}): ActiveFilterModel {
    if (_.get(data, 'attribute.is_user_attribute')) {
      // userAttrSelectValue - PADB_OPT / CUSTOM_OPT
      // textValue - value to search
      const type = (data.userAttrSelectValue === PADB_OPT) ? PADB_FILTER_TYPE : CUSTOM_FILTER_TYPE;
      const id = _.get(data, 'selectValue', ''); // for padb - id of attribute, for custom - 'label'/'value'
      return new ActiveFilterModel(
        data._id,
        null,
        type,
        _.assign({}, data),
        true,
        id,
      );
    } else {
      const attribute = _.find(this.attributesOptions, ['column_name', data.attributeOption]);
      return new ActiveFilterModel(
        data._id,
        null,
        PART_ATTRIBUTES_FILTER_TYPE,
        _.assign({}, attribute, _.omit(data, ['langData'])),
        (attribute.attribute_name === 'description'),
        data.attributeOption,
      );
    }
  }

  public addFiltersAttributes(partAttributesAllFilters: any[]) {
    if (partAttributesAllFilters.length) {
      this.resetAttributes();
      this.addFiltersToAttributesArray(partAttributesAllFilters);
    }
  }

  public removeFilter(filter: any) {
    if (this.attributes.controls.length === 1) {
      this.resetAttribute(this.attributes.controls[0]);
    } else {
      const index = _.findIndex(this.attributes.value, {_id: filter._id});
      if (index >= 0) {
        this.attributes.removeAt(index);
      }
    }
    this.ref.markForCheck();
  }

  public removeFilterById(_id: any) {
    if (this.attributes.controls.length === 1) {
      this.resetAttribute(this.attributes.controls[0]);
    } else {
      const index = _.findIndex(this.attributes.value, {_id});
      if (index >= 0) {
        this.attributes.removeAt(index);
      }
    }
    this.ref.markForCheck();
  }

  // this method configures attribute structure depends on
  // attribute type. If valuesData - set all values of attribute
  public setAttributeStructure(attribute: any, formGroup: any, data: any, valuesData?: any): void {
    /*
      attribute.is_range && attribute.date_picker - from date to date
      attribute.is_text_select - keyword search
      other - just plain select

      attribute.check_for_null - add 'is null' checkbox
    */
    if (!valuesData) {
      this.resetAll(formGroup);
    }
    this.setValueAttribute(attribute, formGroup);

    if (attribute.is_user_attribute) {
      // it's user attribute search - PAdb / Custom
      // https://speedlab.atlassian.net/browse/SPD2-4299
      this.setUserAttribute(attribute, formGroup, data, valuesData);
    } else if (attribute.is_range && attribute.date_picker) {
      this.setAttributeDatePicker(attribute, formGroup, data, valuesData);
    } else if (attribute.nested_fields && attribute.nested_fields.length > 1) {
      this.setAttributeNested(attribute, formGroup, valuesData);
    } else if (attribute.is_text_input_only) {
      this.setAttributeTextOnly(attribute, formGroup, valuesData);
    } else {
      this.setAttributeSelect(attribute, formGroup, data, valuesData);
    }
    if (attribute.check_for_null) {
      this.setAttributeCheckForNull(attribute, formGroup, valuesData);
    }
    if (attribute.multiple_lang_support) {
      this.setAttributeLanguages(attribute, formGroup, valuesData);
    }
    this.ref.markForCheck();
  }

  public isChangeAttributeDisabled(formGroup: any): boolean {
    if (!formGroup) {
      return false;
    }
    const attribute = formGroup.get('attribute').value;
    const isActiveFilter = this.isActiveFilter(formGroup.get('_id').value);

    return attribute ? isActiveFilter : false;
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onAddAnotherClick() {
    this.addAttributeToFormArray();
  }

  onAttributesDropdownOpen(isOpened: boolean, formGroup: UntypedFormGroup, attributeSearchInput: any) {
    if (isOpened) {
      formGroup.get('attributeName').setValue('');
    } else {
      _.delay(() => {
        const option: any = _.find(
          this.attributesOptions,
          ['option', formGroup.get('attributeOption').value]
        );
        formGroup.get('attributeName').setValue(option ? option.title : '');
      }, 300);
    }
  }

  onAttributeChange(selected: any, formGroup: UntypedFormGroup, index: number) {
    this.setAttribute(selected, formGroup);
  }

  onAttributeSelectedOptionChange(selected: any, formGroup: any) {
    if (selected.option !== defaultOptionValue) {
      formGroup.get('selectValue').setValue(selected.option);
    }
  }

  onUserAttributeSelectedOptionChange(selected: any, formGroup: any) {
    if (selected.option !== defaultOptionValue) {
      formGroup.get('userAttrSelectValue').setValue(selected.option);
      this.ref.markForCheck();
    }
    this.updateUserAttr(formGroup);
  }

  onAttributeLangSelectedOptionChange(selected: any, formGroup: any) {
    if (selected.option !== defaultOptionValue) {
      formGroup.get('langValue').setValue(selected.option);
    }
  }

  onNestedAttributeSelectedOptionChange(selected: any, formGroup: any, nestedFormGroup: any, index: number) {
    this.updateNestedAttributeOptions(selected, formGroup, nestedFormGroup, index);
  }

  onCheckboxChange(event: any, formGroup: UntypedFormGroup) {
    const checked = event.target.checked;
    formGroup.get('checkNullValue').setValue(checked);
  }

  onRemoveFilterClick(formGroup: UntypedFormGroup, index?: any) {
    if (formGroup.get('attribute').value) {
      const attribute = formGroup.get('attribute').value;
      const _id = formGroup.get('_id').value;
      this.removeFilterById(_id);
      this.removePartAttribute.emit(_id);
    } else {
      this.attributes.removeAt(index);
    }
  }

  onEnterClick() {
    this.enterClicked.emit();
  }

  /*******************************************
   * COMPONENT PROTECTED METHODS
   *******************************************/

   protected showTextbox(formGroup: any) {
     const attrType = formGroup.value.attributeType;
     return this.isTextSelect(formGroup) ||
       attrType === partAttributesTypes.TEXT ||
       this.isUserAttr(formGroup);
   }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  // this method allows to set attribute
  // when user select attribute or when we need to handle attribute filter
  //  if valuesData is passed - set all attriute values with it
  private setAttribute(attribute: any, formGroup: any, valuesData?: any, _id?: any) {

    if (!formGroup.get('_id').value) {
      formGroup.get('_id').setValue(_id ? _id : currentMomentString());
    }

    if (attribute.is_user_attribute) {
      // https://speedlab.atlassian.net/browse/SPD2-4299
      formGroup.get('attributeName').patchValue(attribute.title);
      formGroup.get('attributeOption').patchValue(attribute.option);
      this.setAttributeStructure(attribute, formGroup, attrOptions, valuesData);
    } else if (attribute.option === defaultOptionValue) {
      this.resetAttribute(formGroup);
    } else {
      formGroup.get('attributeName').patchValue(attribute.title);
      formGroup.get('attributeOption').patchValue(attribute.option);
      if ((attribute.nested_fields && attribute.nested_fields.length) || attribute.is_text_input_only) {
        this.setAttributeStructure(attribute, formGroup, null, valuesData);
      } else if (attribute.options) {
        this.setAttributeStructure(attribute, formGroup, attribute.options, valuesData);
      } else {
        if (!valuesData) {
          this.fetchAttributeOptions.emit({attribute, formGroup});
        } else {
          this.advancedSearchService.fetchAttributeOptions(
            this.status,
            this.brandId,
            attribute.attribute_name,
          )
          .subscribe((data: any) => {
            this.setAttributeStructure(attribute, formGroup, data, valuesData);
          });
        }
      }
    }
    formGroup._selectedAttribute = {option: attribute.option, title: attribute.title};
  }

  // ------ setting attributes of different types ------
  private setAttributeDatePicker(attribute: any, formGroup: any, data: any, valuesData?: any): void {
    formGroup.get('attributeType').setValue(this.partAttributesTypes.DATEPICKER);
    if (valuesData) {
      if (valuesData.dateFromValue) {
        formGroup.get('dateFromValue').setValue(valuesData.dateFromValue);
      }
      if (valuesData.dateToValue) {
        formGroup.get('dateToValue').setValue(valuesData.dateToValue);
      }
    }
  }

  private setAttributeNested(attribute: any, formGroup: any, valuesData?: any): void {
    formGroup.get('attributeType').setValue(this.partAttributesTypes.NESTED);

    const nestedValuesData = valuesData ? valuesData.nestedAttributes : null;

    // change 'attribute_name' for all nested attributes in nested array
    // and change array from [{key: value}, ...] to [value, ...]
    attribute.nestedFieldsArray = _.map(attribute.nested_fields, (nestedField: any) => {
      const name = _.keys(nestedField)[0];
      const value = _.values(nestedField)[0];
      value.attribute_name = name;
      return value;
    });

    attribute.nestedFieldsArray = _.sortBy(attribute.nestedFieldsArray, 'order_index');
    const nestedAttributesFormArray = this.getNestedAttributes(formGroup);

    for (let i = 0; i < attribute.nestedFieldsArray.length; i++) {

      // get value data if it exists
      const attrWithValue = nestedValuesData ? _.find(nestedValuesData, (item: any) => {
        return (item.attribute.attribute_name === attribute.nestedFieldsArray[i].attribute_name);
      }) : null;

      // create new attribute
      const nestedAttributeFormGroup: any = this.addAttributeToFormArray(nestedAttributesFormArray);

      nestedAttributeFormGroup.get('attribute').setValue(attribute.nestedFieldsArray[i]);
      nestedAttributeFormGroup.get('attributeName').setValue(attribute.nestedFieldsArray[i].attribute_name);

      if (!attrWithValue) {
        this.setAttributeStructure(
          nestedAttributeFormGroup.get('attribute').value,
          nestedAttributeFormGroup,
          [],
        );
      } else {
        this.setAttributeStructure(
          nestedAttributeFormGroup.get('attribute').value,
          nestedAttributeFormGroup,
          attrWithValue.selectData,
          attrWithValue,
        );
      }
      this.ref.markForCheck();
    }
    for (let i = 0; i < nestedAttributesFormArray.controls.length; i++) {
      // get value data if it exists
      const attrWithValue = nestedValuesData ? _.find(nestedValuesData, (item: any) => {
        return (item.attribute.attribute_name === attribute.nestedFieldsArray[i].attribute_name);
      }) : null;
      if (!attrWithValue) {
        this.updateNestedAttributeOptions(null, formGroup, nestedAttributesFormArray.controls[i], i);
      }
    }
  }

  private setAttributeSelect(attribute: any, formGroup: any, data: any, valuesData?: any): void {
    formGroup.get('attributeType').setValue(this.partAttributesTypes.SELECT);
    const descriptionDefaultOption = createOption(allOptionValue, 'All descriptions');
    const firstOption = _.chain(data).head().get('option').value();

    if (attribute.attribute_name === 'description') {
      formGroup.get('selectData').setValue(_.concat(descriptionDefaultOption, data));
    } else {
      formGroup.get('selectData').setValue(data);
    }

    if (attribute.is_text_select) {
      formGroup.get('textPlaceholder').setValue(this.getTextPlaceholder(attribute));
      if (valuesData && valuesData.textValue) {
        formGroup.get('textValue').setValue(valuesData.textValue);
      }
    }
    if (valuesData && valuesData.selectValue) {
      formGroup.get('selectValue').setValue(valuesData.selectValue);
    } else if (attribute.attribute_name === 'description') {
      formGroup.get('selectValue').setValue(descriptionDefaultOption.option);
    } else {
      formGroup.get('selectValue').setValue(firstOption);
    }
  }

  private setAttributeTextOnly(attribute: any, formGroup: any, valuesData?: any): void {
    formGroup.get('attributeType').setValue(this.partAttributesTypes.TEXT);
    if (valuesData && valuesData.textValue) {
      formGroup.get('textValue').setValue(valuesData.textValue);
    } else {
      formGroup.get('textPlaceholder').setValue(this.getTextPlaceholder(attribute));
    }
  }

  private setAttributeCheckForNull(attribute: any, formGroup: any, valuesData?: any) {
    if (valuesData) {
      if (valuesData.checkNullValue) {
        formGroup.get('checkNullValue').setValue(valuesData.checkNullValue);
      }
      if (valuesData.hasCheckNull) {
        formGroup.get('hasCheckNull').setValue(valuesData.hasCheckNull);
      }
    } else {
      formGroup.get('hasCheckNull').setValue(true);
    }
  }

  private setAttributeLanguages(attribute: any, formGroup: any, valuesData?: any) {
    const langData: any = attribute.language_options || langStub;
    const defaultOption = createOption(allOptionValue, 'All languages');
    formGroup.get('langData').setValue(
      _.concat(defaultOption, langData)
    );
    if (valuesData && valuesData.langValue) {
      formGroup.get('langValue').setValue(valuesData.langValue);
      const option = _.find(langData, {option: valuesData.langValue});
      formGroup._selectedLangItem = option;
    } else {
      formGroup.get('langValue').setValue('EN');
    }
  }

  private setValueAttribute(attribute: any, formGroup: any) {
    formGroup.get('attribute').setValue(attribute);
  }

  private setUserAttribute(attribute: any, formGroup: any, data: any, valuesData?: any) {
    formGroup.get('attributeType').setValue(this.partAttributesTypes.USER_ATTR);

    // set available User attributes options
    formGroup.get('userAttrSelectData').setValue(attrOptions);

    if (valuesData && valuesData.checkNullValue) {
      formGroup.get('checkNullValue').setValue(valuesData.checkNullValue);
    }

    if (valuesData && valuesData.selectData) {
      formGroup.get('selectData').setValue(valuesData.selectData);
    }

    if (valuesData && valuesData.selectValue) {
      formGroup.get('selectValue').setValue(valuesData.selectValue);
    }

    if (valuesData && valuesData.userAttrSelectValue) {
      formGroup.get('userAttrSelectValue').setValue(valuesData.userAttrSelectValue);
    }

    if (valuesData && valuesData.textValue) {
      formGroup.get('textValue').setValue(valuesData.textValue);
    }
  }

  private updateUserAttr(formGroup: any) {
    const attrName = formGroup.get('attributeOption').value;
    const type = formGroup.get('userAttrSelectValue').value;
    this.fetchProcess.emit(true);
    this.advancedSearchService.fetchAttributeOptions(
        this.status,
        this.brandId,
        attrName,
        {'checkType': type === PADB_OPT ? 'Y' : 'N'},
      )
      .subscribe((data: any) => {
        formGroup.get('selectData').patchValue(data);
        this.fetchProcess.emit(false);
      });

    this.ref.markForCheck();
  }

  // ---------------------------------------------------

  private updateNestedAttributeOptions(selected: any, formGroup: any, nestedFormGroup: any, index: number, hasValue: boolean = false) {
    const nestedAttributes = this.getNestedAttributes(formGroup).controls as UntypedFormGroup[];

    // if attribute value was choosen
    if (selected && selected.option !== defaultOptionValue) {
      // if it's selected - set it's value
      nestedFormGroup.get('selectValue').setValue(selected.option);

      if (index !== nestedAttributes.length - 1 && !hasValue) {
        this.updateNestedAttributeOptions(null, formGroup, nestedAttributes[index + 1], index + 1);
      }

    } else {
      // if attribute value wasn't choosen - need to require it
      this.fetchProcess.emit(true);
      const prevNestedAttributesData = this.getPrevNestedAttributesData(
        nestedAttributes,
        nestedFormGroup.get('attribute').value.order_index,
      );

      this.advancedSearchService.fetchAttributeOptions(
        this.status,
        this.brandId,
        nestedFormGroup.get('attribute').value.attribute_name,
        prevNestedAttributesData,
      )
      .subscribe((data: any) => {

        nestedFormGroup.get('selectData').setValue(data);

        // reset other nested attributes with larger order
        for (let i = index + 1; i < nestedAttributes.length; i++) {
          const nestedAttribute: any = nestedAttributes[i];
        }
        this.fetchProcess.emit(false);

      });
    }
  }

  private resetAll(formGroup: UntypedFormGroup) {
    formGroup.get('textPlaceholder').reset(null);
    formGroup.get('textValue').reset(null);
    formGroup.get('selectData').reset([]);
    formGroup.get('selectValue').reset(null);
    formGroup.get('langData').reset([]);
    formGroup.get('langValue').reset(null);
    formGroup.get('dateFromValue').reset(null);
    formGroup.get('dateToValue').reset(null);
    formGroup.get('userAttrSelectData').reset([]);
    formGroup.get('userAttrSelectValue').reset(null);
    formGroup.get('hasCheckNull').reset(null);
    formGroup.get('checkNullValue').reset(null);
    const nestedAttributes: UntypedFormArray = formGroup.get('nestedAttributes') as UntypedFormArray;
    while (nestedAttributes.controls.length) {
      nestedAttributes.removeAt(0);
    }
  }

  private resetAttribute(formGroup: any) {
    formGroup.get('attribute').reset(null);
    formGroup.get('attributeType').reset(null);
    formGroup.get('attributeName').reset(null);
    formGroup.get('attributeOption').reset(null);
    this.resetAll(formGroup);
  }

  private getPlaceholder(item: any): string {
    return `Select ${item.title_column_name || item.title}`;
  }

  private getTextPlaceholder(attribute: any): string {
    if (attribute.attribute_name === 'description' || attribute.is_user_attribute) {
      return `Search for...`;
    } else {
      return `Enter ${attribute.title_column_name || attribute.title}`;
    }
  }

  private addAttributeToFormArray(formArray?: UntypedFormArray): UntypedFormGroup {
    if (!formArray) {
      formArray = this.attributes;
    }
    // universal group for all types of data
    const formGroup: any = this.fb.group({
      '_id': new UntypedFormControl(), // _id for easy interaction with filters
      'attribute': new UntypedFormControl(), // attribute
      'attributeType': new UntypedFormControl(), // type of attribute: range, datepicker, select, keywordSearch, user_attr
      'attributeName': new UntypedFormControl(), // title of attribute
      'attributeOption': new UntypedFormControl(), // attribute_name
      // input text
      'textPlaceholder': new UntypedFormControl(), // placeholder for text input
      'textValue': new UntypedFormControl(), // value of text input
      // select
      'selectData': new UntypedFormControl([]), // list of possible select options
      'selectValue': new UntypedFormControl(), // selected option
      // nested attributes
      'nestedAttributes': new UntypedFormArray([]), // nested attributes array
      // datepicker
      'dateFromValue': new UntypedFormControl(), // selected date from (if it's range)
      'dateToValue': new UntypedFormControl(), // selected option to (if it's range)
      // user attribute select
      'userAttrSelectData': new UntypedFormControl([]), // list of possible select options
      'userAttrSelectValue': new UntypedFormControl(), // selected option
      // language value
      'langData': new UntypedFormControl([]), // selected option for language
      'langValue': new UntypedFormControl(), // selected option for language
      // checkbox
      'hasCheckNull': new UntypedFormControl(), // if attribute has 'check_null' checkbox
      'checkNullValue': new UntypedFormControl(), // value of 'check_null' checkbox
    });
    formArray.push(formGroup);
    this.ref.markForCheck();
    return formGroup;
  }

  private getAttributeTitle(data: any): string {
    return getFilterTitle({data});
  }

  private getAttribute(formGroup: UntypedFormGroup) {
    return formGroup.get('attribute').value;
  }

  private isAttrSelected(formGroup: UntypedFormGroup) {
    return !formGroup.get('attributeOption') || !formGroup.get('attributeOption').value;
  }

  private isCheckNullSelected(formGroup: UntypedFormGroup): boolean {
    return (formGroup.get('hasCheckNull').value && formGroup.get('checkNullValue').value);
  }

  private isProductGroup(formGroup: UntypedFormGroup): boolean {
    if (!formGroup.get('attribute').value) { return false; }
    return (formGroup.get('attribute').value.attribute_name === 'productGroup');
  }

  private isTextSelect(formGroup: UntypedFormGroup): boolean {
    if (!formGroup.get('attribute').value) { return false; }
    return formGroup.get('attribute').value.is_text_select;
  }

  private isTextOnly(formGroup: UntypedFormGroup): boolean {
    if (!formGroup.get('attribute').value) { return false; }
    return formGroup.get('attribute').value.is_text_input_only;
  }

  private isUserAttr(formGroup: UntypedFormGroup): boolean {
    if (!formGroup.get('attribute').value) { return false; }
    return formGroup.get('attribute').value.is_user_attribute;
  }

  private isLangSupport(formGroup: UntypedFormGroup): boolean {
    if (!formGroup.get('attribute').value) { return false; }
    return formGroup.get('attribute').value.multiple_lang_support;
  }

  private isAlreadyUsed(_id: string): boolean {
    const attributes = this.attributes.value;

    if (_.find(attributes, (item: any) => {
      if (!item._id) {
        return false;
      }
      return item._id === _id;
    })) {
      return true;
    }

    if (this.isActiveFilter(_id)) {
      return true;
    }

    return false;
  }

  private isActiveFilter(_id: string): boolean {
    return _.find(this.filters, (item: any) => {
      if (!item._id) {
        return false;
      }
      return item._id === _id;
    });
  }

  private getNestedAttributes(formGroup: UntypedFormGroup): UntypedFormArray {
    return formGroup.get('nestedAttributes') as UntypedFormArray;
  }

  private getPrevNestedAttributesData(nestedAttributes: UntypedFormGroup[], orderIndex: number) {
    const prevNestedAttributes = _.filter(nestedAttributes, (nestedAttribute: any) => {
      return nestedAttribute.get('attribute').value.order_index < orderIndex;
    });

    return _.map(prevNestedAttributes, (attr: any) => {
      return {
        name: attr.get('attribute').value.attribute_name,
        value: attr.get('selectValue').value, // works only for select nested attributes
      };
    });
  }

  private getAttrFromNestedAttributesArray(nestedAttributesArray: UntypedFormArray, attrName: string): UntypedFormGroup {
    return _.find(nestedAttributesArray.controls, (formGroup: any) => {
      return (formGroup.get('attribute').value.attribute_name === attrName);
    });
  }

  // add filters to `attributes` FormArray
  // to allow user edit them
  private addFiltersToAttributesArray(filters: ActiveFilterModel[]) {
    _.forEach(filters, (filter: ActiveFilterModel, index) => {
      const formGroup = this.addAttributeToFormArray();
      this.setAttribute(filter.data.attribute, formGroup, filter.data, filter._id);
    });
    this.ref.markForCheck();
  }
}
